import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

// Active SKUs (DUE-93). PAIR_1 is the primary one-shot, SUB_MONTH the
// unlimited subscription, TRANSIT_MONTH the daily-transit-only pack.
export const PRODUCT_CODES = [
  "PAIR_1",
  "SUB_MONTH",
  "TRANSIT_MONTH",
] as const;
export type ProductCode = (typeof PRODUCT_CODES)[number];

// Legacy SKUs accepted on `useBuyProduct.buy(...)` for back-compat with
// any in-flight invoice links — never rendered on the new paywall grid.
// DUE-93 deprecated SYNASTRY_1 / SYN_COMP_1 / PACK_2_WEEK; they remain
// here so an existing paywall tab opened mid-deploy keeps working.
export const ALL_PRODUCT_CODES = [
  ...PRODUCT_CODES,
  "SYNASTRY_1",
  "SYN_COMP_1",
  "PACK_2_WEEK",
  "BASIC_SINGLE",
  "COMPOSITE",
  "SYNASTRY_FULL",
  "MONTH_SUB",
  // DUE-411: sold from the Relation screen only, never on the paywall grid.
  "RELATION_NAMED",
  "RELATION_WORDS",
] as const;
export type AnyProductCode = (typeof ALL_PRODUCT_CODES)[number];

// DUE-161 task 6 parity fix: mirrors `api/appstore.ts`'s
// `isAppStoreProductCode` runtime guard so `lib/platform/telegram.ts`'s
// `purchases.buy` can validate its `code: string` input the same way the
// ios adapter does, instead of an unchecked `as AnyProductCode` cast.
export function isAnyProductCode(code: string): code is AnyProductCode {
  return (ALL_PRODUCT_CODES as readonly string[]).includes(code);
}

// The catalog API may return legacy active SKUs during a deploy gap (after
// the backend rolls out but before the admin runs the deactivation PATCHes).
// Accept `AnyProductCode` so the page parses cleanly; the paywall filters
// to `PRODUCT_CODES` at render time so legacy never shows up to users.
export const ProductCatalogItemSchema = z.object({
  code: z.enum(ALL_PRODUCT_CODES),
  stars_price: z.number().int().positive(),
  grants: z.record(z.number().int().nonnegative()),
  title: z.string(),
  description: z.string(),
  display_order: z.number().int(),
});
export type ProductCatalogItem = z.infer<typeof ProductCatalogItemSchema>;

export const ProductCatalogResponseSchema = z.object({
  items: z.array(ProductCatalogItemSchema),
});
export type ProductCatalogResponse = z.infer<typeof ProductCatalogResponseSchema>;

export const PRODUCTS_QUERY_KEY = ["products", "catalog"] as const;

export function useProducts(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: async ({ signal }) =>
      request({
        path: "/api/payments/products",
        schema: ProductCatalogResponseSchema,
        signal,
      }),
    staleTime: 5 * 60_000,
  });
}

export const CreateInvoiceResponseSchema = z.object({
  invoice_link: z.string().url(),
  product_code: z.enum(ALL_PRODUCT_CODES),
  stars_price: z.number().int().positive(),
});
export type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;

export interface CreateInvoiceArgs {
  product_code: AnyProductCode;
  // DUE-89 legacy: present only for the synastry "Open full" handoff
  // (now retired but kept for in-flight clients).
  reading_id?: number;
  tier?: "synastry_full";
}

/**
 * Plain (non-hook) invoice creation, reused by `useCreateInvoiceMutation`
 * below and by the telegram platform adapter's `purchases.buy`
 * (`lib/platform/telegram.ts`), which can't call a React Query hook from
 * outside a component. Keeping this as the single implementation is what
 * makes the adapter's delegation a real wrap rather than a parallel copy.
 */
export function createInvoice(
  args: AnyProductCode | CreateInvoiceArgs,
): Promise<CreateInvoiceResponse> {
  const body: CreateInvoiceArgs =
    typeof args === "string" ? { product_code: args } : args;
  return request({
    method: "POST",
    path: "/api/payments/invoice",
    body,
    schema: CreateInvoiceResponseSchema,
  });
}

export function useCreateInvoiceMutation() {
  return useMutation({ mutationFn: createInvoice });
}
